/**
 * Touch controls for mobile landscape (ARCHITECTURE §8 "Input"): the pure core of the on-screen pad (the web draws
 * it as a plain DOM overlay, apps/client src/input/touch-overlay.ts; a native shell as views), multi-touch, finger
 * sliding between buttons, tap latching.
 *
 *   left thumb:  ◀  ▶  with ▼ below            right thumb:  SPIN  JUMP  ACTION  (SPECIAL above, clear of ACTION)
 *   pause:       small ❚❚ button on the left edge (emits the 'pause' menu action)
 *
 * The left pad ORs overlapping rects on purpose (a thumb on the seam of ▶ and ▼ presses both). The right-thumb
 * buttons are round and exclusive: only the button whose circle is nearest the finger is pressed, so a thumb on the
 * rim of ACTION can never also fire SPECIAL or JUMP. The only right-hand chord is the explicit SPIN button (▼+ACTION).
 *
 * `TouchState` is the pure core (pointer id → bits, hit-testing against cached rects, latching) and is unit-tested
 * without a DOM; an overlay (web `TouchOverlay`) owns the buttons laid out from `TOUCH_LEFT` / `TOUCH_RIGHT` and
 * feeds it pointer events.
 */
import { Btn, type InputFrame } from '@hf/shared';
import { DEVICE_ID, type InputDevice } from './types';

export interface TouchRect {
  bits: number;
  left: number;
  top: number;
  right: number;
  bottom: number;
  /**
   * Round, exclusive button (the right-thumb cluster): the rect is its bounding box incl. slop; a point counts when
   * it lies inside the inscribed circle, and among overlapping round buttons only the nearest one is pressed.
   */
  round?: boolean;
}

/** Pure multi-touch state machine. */
export class TouchState {
  private rects: TouchRect[] = [];
  private readonly pointers = new Map<number, number>();
  private held = 0;
  private latch = 0;
  /** Bits released since the last tick (a re-press of a latched bit in the same tick is a SECOND tap). */
  private released = 0;
  /**
   * Second taps that landed inside one tick (a fast double-tap, or touch events batched behind a JS stall): the sim
   * needs an edge per press, so the re-press is replayed as off for one tick (`gap`) and on for the next.
   */
  private pending = 0;
  private gap = 0;
  /** Called when a pointer newly presses buttons (haptics, activity). */
  onPress: ((bits: number) => void) | null = null;

  setRects(rects: TouchRect[]): void {
    this.rects = rects;
  }

  /**
   * Bits under a point. Square rects may overlap and OR together (a thumb between ▶ and ▼ presses both); round rects
   * are exclusive: the one whose circle edge is nearest inside wins (allocation-free).
   */
  hitTest(x: number, y: number): number {
    let bits = 0;
    let roundBits = 0;
    let roundBest = Infinity;
    for (const r of this.rects) {
      if (x < r.left || x > r.right || y < r.top || y > r.bottom) continue;
      if (!r.round) {
        bits |= r.bits;
        continue;
      }
      const radius = Math.min(r.right - r.left, r.bottom - r.top) / 2;
      const dx = x - (r.left + r.right) / 2;
      const dy = y - (r.top + r.bottom) / 2;
      const depth = Math.sqrt(dx * dx + dy * dy) - radius;
      if (depth <= 0 && depth < roundBest) {
        roundBest = depth;
        roundBits = r.bits;
      }
    }
    return bits | roundBits;
  }

  down(pointerId: number, x: number, y: number): void {
    this.set(pointerId, this.hitTest(x, y));
  }

  move(pointerId: number, x: number, y: number): void {
    if (!this.pointers.has(pointerId)) return;
    this.set(pointerId, this.hitTest(x, y));
  }

  /** Press explicit bits for a pointer (buttons whose meaning is not positional, e.g. the SPIN chord). */
  set(pointerId: number, bits: number): void {
    const before = this.pointers.get(pointerId) ?? 0;
    this.pointers.set(pointerId, bits);
    const fresh = bits & ~before;
    this.recompute();
    if (fresh) {
      const again = fresh & this.latch & this.released;
      if (again) this.pending |= again;
      this.latch |= fresh;
      this.onPress?.(fresh);
    }
  }

  up(pointerId: number): void {
    if (this.pointers.delete(pointerId)) this.recompute();
  }

  releaseAll(): void {
    this.pointers.clear();
    this.held = 0;
    this.latch = 0;
    this.released = 0;
    this.pending = 0;
    this.gap = 0;
  }

  private recompute(): void {
    let h = 0;
    for (const b of this.pointers.values()) h |= b;
    this.released |= this.held & ~h;
    this.held = h;
  }

  get heldBits(): number {
    return this.held;
  }

  /** Bits held by fingers whose bits pass `keep` (the overlay lights the left pad from left-pad fingers only). */
  heldWhere(keep: (bits: number) => boolean): number {
    let h = 0;
    for (const b of this.pointers.values()) if (keep(b)) h |= b;
    return h;
  }

  /** Is some finger pressing exactly these bits (a round button, not a chord that happens to include them)? */
  pressedExactly(bits: number): boolean {
    for (const b of this.pointers.values()) if (b === bits) return true;
    return false;
  }

  sample(): InputFrame {
    return (this.held | this.latch) & ~this.gap;
  }

  endTick(): void {
    // a queued second tap: off this coming tick (gap), on the one after (latch)
    this.latch = this.gap;
    this.gap = this.pending;
    this.pending = 0;
    this.released = 0;
  }
}

export class TouchDevice implements InputDevice {
  readonly id = DEVICE_ID.TOUCH;
  readonly kind = 'touch' as const;
  readonly label = 'Touch controls';
  connected = false;
  constructor(readonly state: TouchState) {}
  sample(): InputFrame {
    return this.connected ? this.state.sample() : 0;
  }
  dispose(): void {
    this.state.releaseAll();
  }
}

// ── Pad layout (shared by every overlay: web DOM, native views) ─────────────────────────────────────────

export interface TouchButtonSpec {
  key: string;
  label: string;
  bits: number;
  /** Web overlay CSS class (native overlays key styles by `key`). */
  cls: string;
  aria: string;
  /** Round exclusive button (right cluster): nearest circle wins, never chorded by overlap. */
  round?: boolean;
}

/** Left-thumb buttons (◀ ▶ ▼): square, overlapping rects OR together. */
export const TOUCH_LEFT: readonly TouchButtonSpec[] = [
  { key: 'left', label: '◀', bits: Btn.L, cls: 'hf-t-left', aria: 'Move left' },
  { key: 'right', label: '▶', bits: Btn.R, cls: 'hf-t-right', aria: 'Move right' },
  { key: 'down', label: '▼', bits: Btn.D, cls: 'hf-t-down', aria: 'Down: truck, fake, fast-fall' },
];
/** Right-thumb buttons: round and exclusive (nearest circle wins). */
export const TOUCH_RIGHT: readonly TouchButtonSpec[] = [
  { key: 'special', label: 'SPECIAL', bits: Btn.S, cls: 'hf-t-special', aria: 'Special', round: true },
  { key: 'jump', label: 'JUMP', bits: Btn.U, cls: 'hf-t-jump', aria: 'Jump', round: true },
  {
    key: 'action',
    label: 'ACTION',
    bits: Btn.A,
    cls: 'hf-t-action',
    aria: 'Action: snap, throw, tackle',
    round: true,
  },
  {
    key: 'spin',
    label: 'SPIN',
    bits: Btn.D | Btn.A,
    cls: 'hf-t-spin',
    aria: 'Spin (down + action)',
    round: true,
  },
];

/** Extra hit slop around each button (px), so a thumb slightly off the edge still counts. */
export const TOUCH_SLOP_PX = 10;
